<template>
  <Background :background class="px-24 py-24 md:py-32" rounded="desktop">
    <div class="m-auto flex w-full flex-col items-center">
      <div class="w-full md:w-[544px]">
        <div class="flex flex-col items-start gap-4 text-left">
          <h2
            v-if="title"
            :id="textToTarget(title, 'heading-2')"
            class="heading-1"
          >
            {{ title }}
          </h2>
          <div v-if="text" class="body-1">{{ text }}</div>
        </div>

        <EmailCollectorForm
          buttonClasses="min-w-full md:min-w-fit"
          class="py-24"
          :placeholder-label
          :source="source ?? 'block'"
          :submitButtonLabel="buttonLabel"
          :toastText
          :tracking="trackingData"
          wrapperClasses="flex-col justify-center gap-8 md:flex-row"
          @submitted="emit('submitted')"
        />

        <div class="caption">
          <RevButtonBase
            aria-controls="collapsibleText"
            :aria-expanded="!isCollapsed"
            class="flex w-full items-start justify-start text-left"
            :icon="isCollapsed ? IconChevronDown : IconChevronUp"
            variant="secondary"
            @click="toggleCollapsedState"
          >
            <div class="h-full shrink-0">
              <component
                :is="isCollapsed ? IconChevronDown : IconChevronUp"
                :aria-hidden="true"
              />
            </div>
            <div>
              <p class="leading-body-1">{{ i18n(translations.toggleLabel) }}</p>
              <div
                id="collapsibleText"
                :class="['block', { hidden: isCollapsed }]"
              >
                <p v-if="legalText">{{ legalText }}</p>

                <RevLink
                  v-if="legalLinkText"
                  class="my-8 block"
                  target="_blank"
                  :to="legalLink"
                >
                  {{ legalLinkText }}
                </RevLink>

                <p v-if="areEmailCollectorTermsVisible" class="mt-8">
                  {{ emailCollectorTerms?.text }}
                </p>
              </div>
            </div>
          </RevButtonBase>

          <RevLink v-if="pdf" class="mt-8 block" target="_blank" :to="pdf.url">
            <span> {{ pdf.label }} </span>
          </RevLink>
        </div>
      </div>
    </div>
  </Background>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevLink } from '@ds/components/Link'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'

import Background from '../../shared-components/Background/Background.vue'
import { textToTarget } from '../../utils/textToTarget'

import translations from './EmailCollector.translations'
import type { EmailCollectorBlockProps } from './EmailCollector.types'
import EmailCollectorForm from './EmailCollectorForm.vue'

const props = defineProps<EmailCollectorBlockProps>()

// "error" event is common to every CMS block in order to hide the block containers
// on demand
const emit = defineEmits(['error', 'submitted'])

const i18n = useI18n()

const currentLocale = useI18nLocale()

const legalLink = `${currentLocale}/legal/data-protection`

const isCollapsed = ref(true)

function toggleCollapsedState() {
  isCollapsed.value = !isCollapsed.value
}

const trackingData = computed(() => ({
  name: props.title,
  ...(props.tracking || {}),
}))

const placeholderLabel = computed(() => {
  return props.placeholderLabel || i18n(translations.inputLabel)
})

const legalText = computed(() => {
  return props.legalText || i18n(translations.legalTextShort)
})

const legalLinkText = computed(() => {
  return props.legalLinkText || i18n(translations.legalLinkText)
})

const areEmailCollectorTermsVisible = computed(() => {
  return !!props.emailCollectorTerms?.text && !props.emailCollectorTerms?.hide
})
</script>
